import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  HStack,
  Image,
  Text,
  useToast,
} from "@chakra-ui/react";
import { PhoneNumberInput } from "components/PhoneNumberInput";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageTaxSupervisorPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const { dispatch, state: { user } } = useContext(Store);
  const { getTaxSupervisor, addTaxSupervisor, updateTaxSupervisor, getCountries, getCities, getAreas, getInstitutions } = useApi();
  const {
    data: taxSupervisor,
    isLoading,
    mutate,
  } = useCache("taxSupervisor", () => getTaxSupervisor(+id || 0));

  const {
    data: areas,
    isLoading: isAreaLoading,
    mutate: mutateArea,
  } = useCache("areas", () => getAreas(1, values.city));

  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () =>
    getInstitutions(1, values.institution || "", "")
  );

  const [profilePictureSrc, setProfilePictureSrc] = useState(null);

  const initialValues = useMemo(() => {
    return {
      email: taxSupervisor?.email || "",
      firstName: taxSupervisor?.first_name || "",
      lastName: taxSupervisor?.last_name || "",
      city: taxSupervisor?.city_id || "",
      area: taxSupervisor?.area_id || "",
      country: taxSupervisor?.country_id || "",
      phoneNumber: taxSupervisor?.phone_number || "",
      profilePicture: taxSupervisor?.profile_picture || "",
      institution: user?.user?.institution_id || taxSupervisor?.institution_id || "",
      submit: null,
    };
  }, [taxSupervisor, user?.user?.institution_id]);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().trim().required("Nom requis"),
    lastName: Yup.string().trim().required("Nom requis"),
    email: Yup.string().email().trim().required("email requis"),
    phoneNumber: Yup.string()
      .trim()
      .required("le numéro de téléphone est requis"),
    country: Yup.string().trim().required("pays requis"),
    city: Yup.string().trim().required("ville requis"),
    area: Yup.string().trim().required("zone requis"),
    institution: Yup.string().trim().required("institution requis"),
    profilePicture: Yup.mixed(),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let formData: any = new FormData();
        if (profilePictureSrc) {
          formData.append(
            "profile_picture",
            values.profilePicture,
            values.profilePicture.name
          );
        }
        formData.append("first_name", values.firstName);
        formData.append("last_name", values.lastName);
        formData.append("phone_number", values.phoneNumber);
        formData.append("country_id", values.country);
        formData.append("city_id", values.city);
        formData.append("area_id", values.area);
        formData.append("email", values.email);
        formData.append("institution_id", values.institution);
        formData.append("is_active", true);
        console.log(values)
        if (id) {
          formData.append("id", id);
        }
        let response: any = null;
        if (id) {
          response = await updateTaxSupervisor(formData);
        } else {
          response = await addTaxSupervisor(formData);
        }
        if (!response.error_code && !response.errors) {
          if (response.id) {
            dispatch(
              showToast({
                toast: toast,
                message: id
                  ? "Superviseur mis à jour avec succès"
                  : "Superviseur ajouté avec succès",
                status: toastTypes.success,
              })
            );
            if (id) {
              mutate(getTaxSupervisor(+id));
              navigate("/tax-supervisors");
            } else {
              navigate("/tax-supervisors");
            }
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        console.log(ex);
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const url = URL.createObjectURL(file);
    setProfilePictureSrc(url);
    setFieldValue("profilePicture", file);
  };

  const {
    data: countries,
    isLoading: isCountryLoading,
    mutate: mutateCountry,
  } = useCache("countries", () => getCountries(1, ""));


  const {
    data: cities,
    isLoading: isCityLoading,
    mutate: mutateCity,
  } = useCache("cities", () => {
    // En mode édition, utiliser le pays du area si disponible
    const countryId = values.country || (id && taxSupervisor?.country_id ? taxSupervisor.country_id : "");
    return getCities(countryId || "", 1, city || "");
  });

  useEffect(() => {
    if (id && taxSupervisor?.country_id && taxSupervisor?.city_id) {
      // En mode édition, charger les villes du pays
      getCities(String(taxSupervisor.country_id), 1, "")
        .then((citiesData) => {
          mutateCity(citiesData || []);
          // Définir la ville si elle existe
          if (taxSupervisor.city_id && !values.city) {
            setFieldValue("city", String(taxSupervisor.city_id));
          }
        });
    }
  }, [id, taxSupervisor?.country_id, taxSupervisor?.city_id, mutateCity, getCities, values.city, setFieldValue]);


  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minH={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier superviseur" : "Créer un superviseur"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="8px" flexDirection="column" alignItems="start">
                  <HStack>
                    <Text color="gray_description" mt="16px">
                      Photo du superviseur
                    </Text>
                    <span style={{ color: "red" }}>*</span>
                  </HStack>
                  <AppInput
                    type="file"
                    accept="image/*"
                    borderWidth="1px"
                    alignSelf="stretch"
                    borderRadius="8px"
                    name="profilePicture"
                    id="profilePicture"
                    onChange={(e: any) => handleFileChange(e)}
                    display="none"
                  />
                  <Flex
                    gap="16px"
                    alignSelf="stretch"
                    alignItems="center"
                    justifyContent={"flex-start"}
                    flexDirection={{ md: "row", base: "column" }}
                  >
                    <Image
                      src={
                        profilePictureSrc || taxSupervisor?.profile_picture
                          ? profilePictureSrc || taxSupervisor?.profile_picture
                          : "images/image_upload_preview.jpg"
                      }
                      alt="product Image"
                      h="100px"
                      alignSelf="center"
                      w={"100px"}
                      fit="contain"
                      borderRadius="12px"
                      onClick={() =>
                        document.getElementById("profilePicture").click()
                      }
                    />
                  </Flex>
                </Flex>
                <Flex gap="16px" flexDirection="column">

                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    {/* Institution */}
                    {!user?.user?.institution_id && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                      >
                        <HStack>
                          <Text color="gray_description">Institution</Text>
                          <span style={{ color: "red" }}>*</span>
                        </HStack>
                        <FormControl
                          isInvalid={
                            !!errors.institution &&
                            (touched.institution as boolean)
                          }
                        ></FormControl>
                        {!isInstitutionLoading && (
                          <AppSelect
                            id="institution"
                            name="institution"
                            onChange={(field: string, value: any) => {
                              setFieldValue(field, value);
                              mutateInstitution(
                                getInstitutions(1, +values.institution || "", value)
                              );
                            }}
                            value={
                              values.institution || initialValues.institution
                            }
                            placeholder="Veuillez sélectionner une institution"
                            options={institutions}
                            isMulti={false}
                            isLoading={isInstitutionLoading}
                            showErrorMessage={Boolean(touched.institution && errors.institution)}
                            errorMessage={errors.institution}
                            onBlur={handleBlur}
                          />
                        )}
                      </Flex>
                    )}
                  </Flex>

                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >

                    {/* Nom  */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Nom</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.lastName && (touched.lastName as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`Nom`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="firstName"
                          name="lastName"
                          autoComplete="lastName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.lastName}
                          showErrorMessage={Boolean(
                            touched.lastName && errors.lastName
                          )}
                          errorMessage={errors.lastName}
                        />
                      </FormControl>
                    </Flex>

                    {/* Prénom */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Prénoms</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.firstName && (touched.firstName as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`Prénoms`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="firstName"
                          name="firstName"
                          autoComplete="firstName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.firstName}
                          showErrorMessage={Boolean(
                            touched.firstName && errors.firstName
                          )}
                          errorMessage={errors.firstName}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >

                    {/* Email */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Email</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.email && (touched.email as boolean)}
                      >
                        <AppInput
                          required
                          placeholder={`ronald.koffikra@gmail.com`}
                          type="email"
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="email"
                          name="email"
                          autoComplete="email"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.email}
                          showErrorMessage={Boolean(
                            touched.email && errors.email
                          )}
                          errorMessage={errors.email}
                        />
                      </FormControl>
                    </Flex>

                    {/* Numéro de téléphone */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Numéro de téléphone
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <PhoneNumberInput
                        name="phoneNumber"
                        onChange={setFieldValue}
                        value={values.phoneNumber}
                        countryDialCode={taxSupervisor?.country_dial_code}
                        onBlur={handleBlur}
                        showErrorMessage={Boolean(
                          touched.phoneNumber && errors.phoneNumber
                        )}
                        errorMessage={errors.phoneNumber}
                      />
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >

                    {/* Pays */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Pays</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.country && (touched.country as boolean)}
                      ></FormControl>
                      {!isCountryLoading && (
                        <AppSelect
                          id="country"
                          name="country"
                          onChange={(field: string, value: any) => {
                            setFieldValue(field, value);
                            setFieldValue("city", "");
                            // Charger les villes du pays sélectionné
                            if (value) {
                              mutateCity(getCities(value || "", 1, ""));
                            } else {
                              mutateCity([]); // Vider les villes si aucun pays
                            }
                          }}
                          value={values.country || initialValues.country}
                          placeholder="Veuillez sélectionner un pays"
                          options={countries}
                          isMulti={false}
                          isLoading={isCountryLoading}
                          showErrorMessage={Boolean(
                            touched.country && errors.country
                          )}
                          errorMessage={errors.country}
                          onBlur={handleBlur}
                        />
                      )}
                    </Flex>

                    {/* Ville */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Ville</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.city && (touched.city as boolean)
                        }
                      ></FormControl>
                      {!isCityLoading && <AppSelect
                        id="city"
                        name="city"
                        handleInputChange={(value) => {
                          setCity(value);
                          mutateCity(getCities(0, 1, value));
                        }}
                        onChange={(field: string, value: any) => {
                          setFieldValue(field, value);
                          setFieldValue("area", "");
                          mutateArea(getAreas(0, value, ""));
                        }}
                        value={values.city || initialValues.city}
                        placeholder="Veuillez sélectionner une ville"
                        options={cities}
                        isMulti={false}
                        isLoading={isCityLoading}
                        showErrorMessage={Boolean(
                          touched.city && errors.city
                        )}
                        errorMessage={errors.city}
                        onBlur={handleBlur}
                      />}
                    </Flex>
                  </Flex>

                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    {/* Commune */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Commune</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.area && (touched.area as boolean)
                        }
                      ></FormControl>
                      {!isAreaLoading && <AppSelect
                        id="area"
                        name="area"
                        onChange={(field: string, value: any) => {
                          setFieldValue(field, value);
                        }}
                        value={values.area || initialValues.area}
                        placeholder="Veuillez sélectionner une commune"
                        options={areas}
                        isMulti={false}
                        isLoading={isAreaLoading}
                        showErrorMessage={Boolean(
                          touched.area && errors.area
                        )}
                        errorMessage={errors.area}
                        onBlur={handleBlur}
                      />}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
